const bimg = (id, crop = "entropy", w = 1400) =>
  `https://images.unsplash.com/${id}?q=80&w=${w}&auto=format&fit=crop&crop=${crop}`;

const A = bimg("photo-1519824145371-296894a0daa9");
const B = bimg("photo-1519823551278-64ac92734fb1");
const C = bimg("photo-1745327883508-b6cd32e5dde5");
const D = bimg("photo-1596178060671-7a80dc8059ea");

export const PAIN_POSTS = [
  {
    slug: "massage-for-chronic-muscle-tension",
    title: "Massage for Chronic Muscle Tension: How Many Sessions Will You Need?",
    keyword: "massage for chronic muscle tension",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: A,
    excerpt:
      "Tension that has lived in the body for years does not leave in one hour. An honest look at how many sessions chronic tension usually takes — and why.",
    body: [
      {
        p: [
          "Chronic muscle tension is tension that has become a habit of the tissue. The muscle stays partly contracted even at rest, circulation slows, and the area becomes dense and tender. By the time it bothers you enough to book, it has usually been building for months or years.",
        ],
      },
      {
        h: "A realistic timeline",
        p: [
          "Most people feel a clear release after the first session — lighter shoulders, easier movement. But tissue that took years to tighten changes over weeks, not minutes. For long-standing tension, a short course of 4 to 6 sessions, spaced one to two weeks apart, is a realistic start.",
          "After that, most clients move to maintenance: one session every 4 to 6 weeks keeps the tissue from rebuilding its old habits.",
        ],
      },
      {
        h: "What makes the results last",
        p: [
          "The sessions create the release; your days decide whether it stays. Small things — a screen at eye level, a two-minute stretch, warmer sleep positions — protect the work far better than any single appointment can.",
        ],
      },
    ],
    note:
      "If muscle tension comes with unexplained weakness, numbness or pain that wakes you at night, see a physician before booking. Massage may help reduce symptoms, but those signs need medical attention first.",
  },
  {
    slug: "sore-after-deep-tissue-massage",
    title: "Why Your Muscles Ache After a Deep Tissue Massage",
    keyword: "sore after deep tissue massage",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: B,
    excerpt:
      "Post-massage soreness is common, normal and temporary. What causes it, how it differs from a bad sign, and how to ease it.",
    body: [
      {
        p: [
          "Feeling tender the day after a deep tissue massage surprises many first-time clients. The sensation is similar to the day after a good workout — a dull, warm ache in the areas that were worked. Therapists call it post-massage soreness, and in most cases it is simply the tissue responding to real change.",
        ],
      },
      {
        h: "Why it happens",
        p: [
          "Deep tissue work creates tiny, controlled stress in muscle fibres and fascia — the same mechanism that makes muscles sore after exercise. Blood flow increases, metabolic byproducts move, and the nervous system recalibrates the area. All of that can feel like mild bruising for a day or two.",
        ],
      },
      {
        h: "Normal soreness vs. a red flag",
        p: [
          "Normal soreness feels dull and warm, peaks within 24–48 hours and fades steadily. Sharp pain, visible bruising, or soreness that worsens after day two is not the goal of treatment — tell your therapist, so pressure is adjusted next time.",
          "Water, a warm bath or shower, and gentle movement are the best remedies. The ache should be a footnote, not the headline.",
        ],
      },
    ],
    note:
      "Severe pain, swelling, or symptoms that feel wrong for your body always deserve a conversation — with your therapist and, if needed, your physician.",
  },
  {
    slug: "how-long-sore-after-deep-tissue-massage",
    title: "How Long Does Soreness Last After a Deep Tissue Massage?",
    keyword: "how long sore after deep tissue massage",
    category: "Deep Tissue & Chronic Pain",
    read: "3 min read",
    img: C,
    excerpt:
      "The honest timeline: when post-massage soreness peaks, when it should fade, and when it means the pressure should change next time.",
    body: [
      {
        p: [
          "For most people, post-deep-tissue soreness follows a predictable arc: it appears within a few hours of the session, peaks the next morning, and fades over 24 to 72 hours. By day three, the ache should be gone — replaced by the looseness you booked for.",
        ],
      },
      {
        h: "What changes the timeline",
        p: [
          "First sessions after a long break tend to produce more soreness — the tissue is not used to being worked. Hydration, sleep and gentle movement shorten recovery; going straight to an intense workout lengthens it.",
          "Clients who come regularly usually report less and less soreness over time, even with the same pressure. The tissue adapts.",
        ],
      },
      {
        h: "When it is too much",
        p: [
          "Soreness lasting beyond three days, or pain that felt sharp during the session itself, means the pressure was too strong for your tissue that day. That is useful information — tell your therapist, and the next session will be calibrated differently. Deeper is not better; appropriate is better.",
        ],
      },
    ],
    note: null,
  },
  {
    slug: "what-to-do-after-deep-tissue-massage",
    title: "What to Do After a Deep Tissue Massage",
    keyword: "what to do after deep tissue massage",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: D,
    excerpt:
      "The session ends when you leave the table — but the results are decided in the 24 hours after. A simple aftercare ritual that protects the work.",
    body: [
      {
        p: [
          "A deep tissue session opens a window: muscles are softer, circulation is up, the nervous system is calm. What you do in the following day either extends that window or lets it close early. The good news — the ritual is simple.",
        ],
      },
      {
        h: "The first hours",
        p: [
          "Drink water — not because massage “flushes toxins” (it does not), but because well-hydrated tissue simply recovers better. Keep the rest of the day gentle: a walk is ideal, a heavy workout is not. If an area feels worked, warmth — a bath or a heating pad on low — settles it beautifully.",
        ],
      },
      {
        h: "The day after",
        p: [
          "Mild soreness is normal; move through it gently rather than freezing. Notice what changed — easier turning of the neck, a quieter lower back — and tell your therapist at the next visit. That feedback shapes the next session.",
          "And if the session gave you a rare, quiet hour, consider protecting those too: they are part of why the body heals.",
        ],
      },
    ],
    note: null,
  },
  {
    slug: "how-often-should-you-get-deep-tissue-massage",
    title: "How Often Should You Get a Deep Tissue Massage?",
    keyword: "how often should you get deep tissue massage",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: A,
    excerpt:
      "Weekly, monthly, or only when it hurts? How to find a rhythm that actually matches your body, your goals and your budget.",
    body: [
      {
        p: [
          "The honest answer: it depends on what the massage is for. Deep tissue used as repair and deep tissue used as maintenance follow different clocks.",
        ],
      },
      {
        h: "While you are fixing something",
        p: [
          "For a specific problem — a stubborn knot, chronic back tightness, postural pain — sessions work best close enough together that each one builds on the last. Once a week or every two weeks, for three to six visits, is the rhythm that typically changes things.",
        ],
      },
      {
        h: "Once you are maintaining",
        p: [
          "When the original complaint has quieted, most bodies do well with one session every four to six weeks. Desk workers with heavy tension often prefer every three to four; athletes in hard training blocks sometimes keep a weekly slot during the season.",
          "The right interval is the one where you arrive at the table still feeling the benefits of the last visit — not starting from zero again.",
        ],
      },
    ],
    note: null,
  },
  {
    slug: "is-deep-tissue-massage-supposed-to-hurt",
    title: "Is Deep Tissue Massage Supposed to Hurt?",
    keyword: "is deep tissue massage supposed to hurt",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: B,
    excerpt:
      "“No pain, no gain” does not apply on the massage table. Where the line between productive pressure and real pain actually sits.",
    body: [
      {
        p: [
          "Somewhere along the way, deep tissue massage picked up a reputation for being painful — as if wincing were proof of quality. It is not. Effective deep work and painful deep work are not the same thing, and confusing them leads to guarded muscles and poor results.",
        ],
      },
      {
        h: "The line that matters",
        p: [
          "Therapists often describe the right zone as “good pain”: a strong, specific pressure you can breathe through, that feels like something is finally being addressed. The wrong zone is anything sharp, bracing, or that makes you hold your breath or tense against the table.",
          "When you brace, the muscle protects itself — and the therapist is suddenly working against you instead of with you.",
        ],
      },
      {
        h: "Your role in the pressure",
        p: [
          "Speak. “A little less” or “stay right there” are the most therapeutic words in the room. At Royal Essence, pressure is calibrated continuously — a session is a conversation, part spoken, part through the tissue itself.",
        ],
      },
    ],
    note:
      "Pain during massage that is sharp, burning, or radiating is never a goal. Tell your therapist immediately — and if unusual pain persists after sessions, consult your physician.",
  },
  {
    slug: "deep-tissue-massage-for-muscle-knots",
    title: "Deep Tissue Massage for Muscle Knots and Trigger Points",
    keyword: "deep tissue massage for muscle knots",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: C,
    excerpt:
      "Those small, stubborn knots that refer pain to surprising places. What trigger points are and how deep, patient pressure releases them.",
    body: [
      {
        p: [
          "A muscle knot — clinically, a trigger point — is a small band of fibres stuck in contraction. It feels like a pea or a cord under the skin, it is tender when pressed, and it has a strange habit of sending pain somewhere else: a knot in the shoulder can ache behind the eye; one in the glute can mimic sciatica.",
        ],
      },
      {
        h: "How they are released",
        p: [
          "Trigger points respond to sustained, specific pressure — not rubbing, not force. The therapist finds the point, applies slow pressure until the tissue softens (often 30 to 90 seconds), waits for the characteristic release, then lengthens the muscle around it.",
          "One knot may release in a session; a pattern of knots built over years usually needs a short series, because each layer guards the next.",
        ],
      },
      {
        h: "Why they come back",
        p: [
          "Knots are usually employees, not bosses — they form where the body compensates for posture, stress or an old injury. Lasting change pairs the release with small changes at the desk, in the car, in training. Your therapist will point out the two or three that matter for you.",
        ],
      },
    ],
    note:
      "A lump that is fixed, hard, growing, or painless-but-persistent is not a trigger point — have it checked by a physician.",
  },
  {
    slug: "massage-for-glute-pain",
    title: "Massage for Glute Pain",
    keyword: "massage for glute pain",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: D,
    excerpt:
      "The glutes work all day and complain quietly — until they do not. Why glute pain happens, and what a session actually addresses.",
    body: [
      {
        p: [
          "Glute pain rarely starts in the glutes. Long sitting shortens the hip flexors and puts the glute muscles to sleep; then a run, a hike or a heavy week at the gym asks them to work overtime without preparation. The result is a deep, stubborn ache in the buttock — sometimes travelling toward the hip or down the leg.",
        ],
      },
      {
        h: "What a session addresses",
        p: [
          "Effective glute work is layered: slow compression and deep strokes through gluteus maximus, precise work on the smaller stabilizers beneath it (piriformis and its neighbours), and always the surrounding cast — hip flexors, lower back and hamstrings — because the glute never tightens alone.",
          "Work here is done through a sheet with clear communication; positioning is side-lying or prone, always comfortable.",
        ],
      },
      {
        h: "The look-alike to know",
        p: [
          "Deep glute pain can imitate sciatica, and sciatica can imitate glute pain. A skilled therapist can often tell them apart by how the pain behaves — but true radiating pain, numbness or weakness is a physician's territory first.",
        ],
      },
    ],
    note:
      "Buttock pain with numbness, tingling or weakness down the leg needs medical evaluation before massage. Massage may help reduce muscular symptoms — it does not treat nerve compression.",
  },
  {
    slug: "massage-for-tight-hips",
    title: "Massage for Tight Hips and Limited Mobility",
    keyword: "massage for tight hips",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: A,
    excerpt:
      "Sitting shortens the front of the hips; training tightens the rest. How massage restores hip mobility — and why the hips matter for your back and knees.",
    body: [
      {
        p: [
          "Tight hips are the quiet tax of modern life. Hours of sitting hold the hip flexors in a shortened position; running, cycling and lifting load the rest of the hip without ever lengthening it. Over time the joint loses range — and the lower back and knees start paying the bill.",
        ],
      },
      {
        h: "What a hip-focused session includes",
        p: [
          "Hip work is a full-circle affair: the flexors at the front, the glutes at the back, the rotators deep underneath, and the IT band and thigh muscles that argue with all of them. Slow deep tissue strokes and myofascial release restore glide between the layers; gentle movement of the leg during the work helps the joint remember its range.",
        ],
      },
      {
        h: "Keeping the range",
        p: [
          "A ninety-second daily habit — a low lunge stretch, a figure-four stretch — preserves hip mobility far better than a heroic monthly stretch. Your therapist will show you the one or two that match your pattern.",
        ],
      },
    ],
    note:
      "Hip pain with clicking that locks, night pain, or pain after a fall should be assessed medically before massage.",
  },
  {
    slug: "deep-tissue-massage-for-sciatica",
    title: "Can You Get a Deep Tissue Massage with Sciatica?",
    keyword: "deep tissue massage for sciatica",
    category: "Deep Tissue & Chronic Pain",
    read: "5 min read",
    img: B,
    excerpt:
      "Sometimes the answer is yes, sometimes it is “not yet”. How to tell muscular sciatica-like pain from the real thing — and where massage fits.",
    body: [
      {
        p: [
          "“Sciatica” describes pain travelling from the lower back or buttock down the leg. Two very different situations produce it: the sciatic nerve compressed at the spine (a disc or joint issue), or the nerve irritated on its path through tight deep hip muscles — sometimes called pseudo-sciatica. The distinction matters, because massage fits them differently.",
        ],
      },
      {
        h: "Where massage may help",
        p: [
          "When tight piriformis and glute muscles are the irritant, careful deep tissue work can reduce that compression and ease symptoms significantly. Work is slow and conservative — the goal is to quiet the area, not to provoke it. The lower back, hips and legs are all part of the session.",
        ],
      },
      {
        h: "Where caution comes first",
        p: [
          "True disc-related sciatica is a different case. Massage can still be part of your comfort toolkit — gently, around the guarded muscles — but only after a medical assessment, and never as a replacement for it. Progressive numbness, weakness, or loss of bladder or bowel control are emergency signs, not massage signs.",
        ],
      },
    ],
    note:
      "If you suspect sciatica, see your physician first for a diagnosis. With their green light, massage may help reduce muscular contributors and make recovery far more comfortable.",
  },
];
